import { useCallback, useState } from "react";

interface UseCursorPaginationOptions {
  initialLimit?: number;
}

interface CursorPageResult {
  nextCursor?: string | null;
  previousCursor?: string | null;
}

export function useCursorPagination({ initialLimit = 20 }: UseCursorPaginationOptions = {}) {
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(undefined);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [previousCursor, setPreviousCursor] = useState<string | null>(null);
  const [limit, setLimitState] = useState(initialLimit);

  const hasPrevious = previousCursor != null;
  const hasMore = nextCursor != null;

  const setPage = useCallback((page: CursorPageResult) => {
    setNextCursor(page.nextCursor ?? null);
    setPreviousCursor(page.previousCursor ?? null);
  }, []);

  const goNext = useCallback(() => {
    if (!nextCursor) return;
    setCurrentCursor(nextCursor);
  }, [nextCursor]);

  const goPrevious = useCallback(() => {
    if (!previousCursor) return;
    setCurrentCursor(previousCursor);
  }, [previousCursor]);

  const setLimit = useCallback((newLimit: number) => {
    setLimitState(newLimit);
    setCurrentCursor(undefined);
    setNextCursor(null);
    setPreviousCursor(null);
  }, []);

  const reset = useCallback(() => {
    setCurrentCursor(undefined);
    setNextCursor(null);
    setPreviousCursor(null);
  }, []);

  return {
    cursor: currentCursor,
    limit,
    hasPrevious,
    hasMore,
    setPage,
    goNext,
    goPrevious,
    setLimit,
    reset,
  };
}
